import type { ConfigIssue } from '../catalog/issue';
import { SEVERITY, issueMessage, severityOf } from '../catalog/issue';
import type { RcuConfig } from '../model';
import { VALIDATION_RULES } from './rules';

const SEVERITY_ORDER = {
  [SEVERITY.ERROR]: 0,
  [SEVERITY.WARNING]: 1,
  [SEVERITY.INFO]: 2,
} as const;

/** Every problem found in the configuration, worst first. */
export const validateConfig = (config: RcuConfig): readonly ConfigIssue[] =>
  VALIDATION_RULES.flatMap((rule) => rule(config)).sort(
    (left, right) => SEVERITY_ORDER[severityOf(left)] - SEVERITY_ORDER[severityOf(right)],
  );

export const errorsOf = (issues: readonly ConfigIssue[]): readonly ConfigIssue[] =>
  issues.filter((issue) => severityOf(issue) === SEVERITY.ERROR);

export const warningsOf = (issues: readonly ConfigIssue[]): readonly ConfigIssue[] =>
  issues.filter((issue) => severityOf(issue) === SEVERITY.WARNING);

/** True when nothing blocks deployment. */
export const isDeployable = (config: RcuConfig): boolean =>
  errorsOf(validateConfig(config)).length === 0;

/**
 * Development guard: throws if a command produced a configuration that breaks
 * an invariant. Used after every commit in DEV and in the test suite.
 */
export const assertConfigInvariants = (config: RcuConfig): void => {
  const errors = errorsOf(validateConfig(config));
  if (errors.length > 0) {
    throw new Error(`Invalid RCU configuration:\n${errors.map(issueMessage).join('\n')}`);
  }
};
